import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import PaginationInMemory from "../../shared-components/PaginationInMemory";

import { usePermissionValidator } from "../../api/UserPermissionValidators";
import {
  FilterSortHeader,
  FilterValues,
  GenericObject,
} from "../FilterSortHeader";
import { ThreeSixtyFinishedRow } from "../../api/ThreeSixty";
import {
  useGetThreeSixtyBlueprints,
  useGetThreeSixtyCompanies,
  useGetThreeSixtyFinishedNameFilterOptions,
} from "../../api/ThreeSixtyAPI";
import { useGetThreeSixtyFinishedListLists360 } from "../../api/threeSixtyLists360";
import { ThreeSixtyFinishedRowRenderer } from "../ThreeSixtyFinishedRowRenderer";
import SkeletonGrid from "../../shared-components/SkeletonGrid";

const PAGE_SIZE = 20;

export function applyFinishedClientFilters(
  rows: ThreeSixtyFinishedRow[],
  activeFilters: FilterValues[]
): ThreeSixtyFinishedRow[] {
  let result = [...rows];
  for (const af of activeFilters) {
    const { field, values } = af;
    if (values.length === 0) continue;
    if (field === "name") {
      // Each entry is one checked name (or one typed search): entries match as OR,
      // words within an entry as AND, so a typed "jane doe" still narrows.
      const wordGroups = values
        .map((v) =>
          String(v)
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
        )
        .filter((words) => words.length > 0);
      if (wordGroups.length === 0) continue;
      result = result.filter((row) => {
        const name = row.name.toLowerCase();
        return wordGroups.some((words) => words.every((w) => name.includes(w)));
      });
    } else if (field === "custom_id") {
      result = result.filter((row) =>
        values.some((v) =>
          String(row.custom_id).toLowerCase().includes(String(v).toLowerCase())
        )
      );
    } else if (field === "company_uuid") {
      result = result.filter((row) => values.includes(row.company_uuid));
    } else if (field === "blueprint_uuid") {
      result = result.filter((row) => values.includes(row.blueprint_uuid));
    } else if (field === "finished_at") {
      const d = values[0];
      if (d instanceof Date) {
        const ymd = d.toISOString().split("T")[0];
        result = result.filter(
          (row) => row.finished_at && row.finished_at.startsWith(ymd)
        );
      }
    }
  }
  return result;
}

function sortFinishedRows(
  rows: ThreeSixtyFinishedRow[],
  sortField: string,
  reverse: boolean
): ThreeSixtyFinishedRow[] {
  if (!sortField) {
    // Legacy GET /360/finished used sort=-due_date when sortField was empty.
    const mult = -1;
    return [...rows].sort((a, b) => {
      const da = Date.parse(String(a.due_date));
      const db = Date.parse(String(b.due_date));
      const cmp =
        (Number.isFinite(da) ? da : 0) - (Number.isFinite(db) ? db : 0);
      return cmp * mult;
    });
  }
  const mult = reverse ? -1 : 1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (sortField === "finished_at" || sortField === "due_date") {
      const da = Date.parse(
        String((a as unknown as Record<string, string>)[sortField])
      );
      const db = Date.parse(
        String((b as unknown as Record<string, string>)[sortField])
      );
      cmp = (Number.isFinite(da) ? da : 0) - (Number.isFinite(db) ? db : 0);
    } else {
      const va = String(
        (a as unknown as Record<string, unknown>)[sortField] ?? ""
      );
      const vb = String(
        (b as unknown as Record<string, unknown>)[sortField] ?? ""
      );
      cmp = va.localeCompare(vb);
    }
    return cmp * mult;
  });
}

type ThreeSixtyFinishedProps = {};
const ThreeSixtyFinished = (_props: ThreeSixtyFinishedProps) => {
  const [cancel, setCancel] = useState<string[]>([]);
  function toggleCancel(value: string): number {
    if (cancel.includes(value)) {
      const removed: string[] = cancel.filter((x) => x !== value);
      setCancel(removed);
      return -1;
    }
    setCancel([...cancel, value]);
    return 1;
  }

  const perms = usePermissionValidator();
  const location = useLocation();
  const force = Boolean(
    (location.state as { force?: boolean } | undefined)?.force
  );

  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("due_date");
  const [reverse, setReverse] = useState<boolean>(true);
  const [activeFilters, setActiveFilters] = useState<FilterValues[]>([]);
  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;

  const {
    data: allRows,
    mutate: loadAll,
    isLoading: allRowsLoading,
  } = useGetThreeSixtyFinishedListLists360();
  const { mutateAsync: getNameFilterOptions } =
    useGetThreeSixtyFinishedNameFilterOptions();

  const loadNameFilterOptions = useCallback(
    async (search: string): Promise<GenericObject[]> => {
      const currentFilters = activeFiltersRef.current;
      const refs = currentFilters.find(
        (filter) => filter.field === "custom_id"
      );
      const dates = currentFilters.find(
        (filter) => filter.field === "finished_at"
      );
      const companies = currentFilters.find(
        (filter) => filter.field === "company_uuid"
      );
      const blueprints = currentFilters.find(
        (filter) => filter.field === "blueprint_uuid"
      );
      const names = await getNameFilterOptions({
        search,
        date: dates?.values[0],
        ref: refs?.values.join(","),
        company_uuids: companies?.values ?? [],
        blueprint_uuids: blueprints?.values ?? [],
      });
      return names.map((name) => ({ name }));
    },
    [getNameFilterOptions]
  );

  useEffect(() => {
    loadAll(force);
  }, [loadAll, force]);

  const { data: companyData, mutate: companyDataGet } =
    useGetThreeSixtyCompanies();

  useEffect(() => {
    companyDataGet();
  }, [companyDataGet]);

  const [companiesForFilters, setCompaniesForFilters] = useState<
    GenericObject[]
  >([]);
  useEffect(() => {
    const newCompanies = [];
    for (const co of companyData || []) {
      newCompanies.push({
        company_uuid: co.uuid,
        company_name: co.name,
      });
    }
    setCompaniesForFilters(newCompanies);
  }, [companyData]);

  const { data: blueprintData } = useGetThreeSixtyBlueprints();

  const [blueprintsForFilters, setBlueprintsForFilters] = useState<
    GenericObject[]
  >([]);
  useEffect(() => {
    const newBlueprints = [];
    for (const bp of blueprintData || []) {
      newBlueprints.push({
        blueprint_uuid: bp.uuid,
        blueprint_name: bp.name,
      });
    }
    newBlueprints.sort((a, b) =>
      a.blueprint_name.localeCompare(b.blueprint_name)
    );
    setBlueprintsForFilters(newBlueprints);
  }, [blueprintData]);

  function setFilters(field: string, values: any[]) {
    const newFilters: FilterValues[] = activeFilters.filter(
      (a) => a.field !== field
    );
    if (values.length > 0) {
      newFilters.push({
        field: field,
        values: values,
      });
    }
    setActiveFilters(newFilters);
    setPage(1);
  }

  function setSortBy(field: string) {
    setPage(1);
    if (field === sortField) {
      if (reverse) {
        setSortField("");
      }
      setReverse(!reverse);
    } else {
      setSortField(field);
      setReverse(false);
    }
  }

  const filteredRows = useMemo(
    () => applyFinishedClientFilters(allRows ?? [], activeFilters),
    [allRows, activeFilters]
  );

  const sortedRows = useMemo(
    () => sortFinishedRows(filteredRows, sortField, reverse),
    [filteredRows, sortField, reverse]
  );

  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page]
  );

  if (!perms.canView360) {
    return <Navigate to="/unauthorized" replace />;
  }

  const loadingOrPreparingData = allRowsLoading && allRows == null;

  return (
    <div>
      <div className="absolute right-4 -top-8">
        <ArrowPathIcon
          className="w-5 stroke-green-500"
          onClick={() => loadAll(true)}
        />
      </div>
      <div className="flex gap-2 text-gray-500 text-xs font-medium bg-gray-50 p-2 border rounded-t-xl border-gray-300 uppercase">
        <FilterSortHeader
          headers={[
            {
              display: "Name",
              value_field: "name",
              filterable: true,
              sortable: true,
              searchable: true,
              loadFilterOptions: loadNameFilterOptions,
              filterOptionsDebounceMs: 1000,
            },
            {
              display: "Report ID",
              value_field: "request_uuid",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          reverse={reverse}
          sortCallback={setSortBy}
          data={sortedRows}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Insights",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Company",
              key_field: "company_uuid",
              value_field: "company_name",
              filterable: true,
              sortable: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={companiesForFilters}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Check Type",
              key_field: "blueprint_uuid",
              value_field: "blueprint_name",
              filterable: true,
              sortable: true,
            },
            {
              display: "Behavior Kit",
              value_field: "behavior_kit_name",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={blueprintsForFilters}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Met SLA?",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Date Completed",
              value_field: "finished_at",
              filterable: true,
              sortable: true,
              datePick: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Client Ref #",
              value_field: "custom_id",
              filterable: true,
              sortable: true,
              searchable: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={sortedRows}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Actions",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
      </div>
      <div className="bg-white border rounded-b-xl border-gray-300">
        {loadingOrPreparingData ? (
          <div className="p-2">
            <SkeletonGrid numberOfRows={8} />
          </div>
        ) : sortedRows.length === 0 && !allRowsLoading ? (
          <div className="p-2">no data found</div>
        ) : (
          <div className="text-xs">
            {pageRows.map((row) => (
              <ThreeSixtyFinishedRowRenderer
                key={row.request_uuid}
                row={row}
                cancel={cancel}
                toggleCancel={toggleCancel}
              />
            ))}
          </div>
        )}
      </div>
      {!loadingOrPreparingData && sortedRows.length > 0 && (
        <div className="pb-4">
          <PaginationInMemory
            totalCount={sortedRows.length}
            currentPage={page}
            pageSize={PAGE_SIZE}
            showDescription={true}
            pageChange={setPage}
            activeStyle="bg-green-50"
          />
        </div>
      )}
    </div>
  );
};

export default ThreeSixtyFinished;
